import { useEffect, useState } from 'react';
import {
  DEAL_LINE_ITEM_STATUSES,
  DEAL_LINE_ITEM_TYPES,
  DEAL_RATE_UNITS,
  addDays,
  formatHours,
  parseHours,
  toWireDate,
  type DealDetail,
  type DealLineItem,
  type DealLineItemStatus,
  type DealLineItemType,
  type DealRateUnit,
} from '@forecastify/shared';
import { Button, Dialog, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { lineStatusLabel, lineTypeLabel, rateUnitLabel } from './deal-kit';
import { amountText, parseAmount, useActiveCurrencies } from './plan-kit';
import type { DealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · *Új sortétel* — `phase6/15b-deal-lineitem-new.png`.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The fields follow the type, as the mockup's `dliIsTm` / `dliIsUnit` do.** Hours are work,
 * so only a fixed or a time-and-material line carries an estimate; a price per unit is what makes
 * T&M, a licence and an option what they are, so a fixed line sends no rate and no quantity. A
 * hidden field is sent as its empty value rather than as whatever was typed before the type
 * changed — what the dialog shows is what is saved.
 */

/** Hours are planned on these; a licence and an option are priced, not worked (`dealLiHasWork`). */
export function lineHasWork(type: DealLineItemType): boolean {
  return type === 'fixed' || type === 'tm';
}

export function LineItemDialog({
  open,
  deal,
  line,
  record,
  defaultCurrency,
  onClose,
}: {
  readonly open: boolean;
  readonly deal: DealDetail;
  /** null: a new line item. */
  readonly line: DealLineItem | null;
  readonly record: DealRecord;
  readonly defaultCurrency: string;
  readonly onClose: () => void;
}) {
  const t = useT();
  const currencies = useActiveCurrencies([
    line?.currencyCode ?? '',
    defaultCurrency,
    deal.currencyCode,
  ]);

  const [name, setName] = useState('');
  const [type, setType] = useState<DealLineItemType>('fixed');
  const [status, setStatus] = useState<DealLineItemStatus>('draft');
  const [milestoneId, setMilestoneId] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [estimate, setEstimate] = useState('');
  const [costBudget, setCostBudget] = useState('');
  const [rate, setRate] = useState('');
  const [rateUnit, setRateUnit] = useState<DealRateUnit>('hour');
  const [quantity, setQuantity] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* A fresh form every time it opens — the line's own values, or the mockup's defaults. */
  useEffect(() => {
    if (!open) return;
    const today = toWireDate(new Date());
    setName(line?.name ?? '');
    setType(line?.type ?? 'fixed');
    setStatus(line?.status ?? 'draft');
    setMilestoneId(line?.milestoneId ?? '');
    setStartsOn(line === null ? today : (line.startsOn ?? ''));
    setEndsOn(line === null ? addDays(today, 13) : (line.endsOn ?? ''));
    setEstimate(
      line === null || Number(line.estimateHours) === 0 ? '' : formatHours(line.estimateHours),
    );
    setCostBudget(amountText(line?.costBudget ?? null));
    setRate(amountText(line?.rate ?? null));
    setRateUnit(line?.rateUnit ?? 'hour');
    const storedQuantity = line?.quantity ?? null;
    setQuantity(storedQuantity === null ? '' : String(Number(storedQuantity)));
    setAmount(amountText(line?.amount ?? null));
    setCurrency(line?.currencyCode ?? defaultCurrency);
    setError(null);
  }, [open, line, defaultCurrency]);

  const hasWork = lineHasWork(type);
  const priced = type !== 'fixed';
  const rateValue = parseAmount(rate);
  const quantityValue = parseAmount(quantity);
  const canCompute =
    priced &&
    rate.trim() !== '' &&
    quantity.trim() !== '' &&
    rateValue !== null &&
    quantityValue !== null;

  const edit =
    (setter: (value: string) => void) =>
    (event: { target: { value: string } }): void => {
      setter(event.target.value);
      setError(null);
    };

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealPlan.error.name'));
      return;
    }
    if (startsOn !== '' && endsOn !== '' && endsOn < startsOn) {
      setError(t('dealPlan.error.dates'));
      return;
    }
    const hours = !hasWork || estimate.trim() === '' ? 0 : parseHours(estimate);
    if (hours === null) {
      setError(t('dealPlan.error.hours'));
      return;
    }
    const budgetValue = parseAmount(costBudget);
    const amountValue = parseAmount(amount);
    if (budgetValue === null || amountValue === null || (priced && rateValue === null)) {
      setError(t('deals.error.value'));
      return;
    }
    if (priced && quantityValue === null) {
      setError(t('dealPlan.error.quantity'));
      return;
    }
    setBusy(true);
    const refusal = await record.write(
      line === null ? '/line-items' : `/line-items/${line.id}`,
      line === null ? 'POST' : 'PUT',
      {
        name: name.trim(),
        type,
        status,
        milestoneId: milestoneId === '' ? null : milestoneId,
        startsOn: startsOn === '' ? null : startsOn,
        endsOn: endsOn === '' ? null : endsOn,
        estimateHours: hours,
        costBudget: budgetValue,
        amount: amountValue,
        currencyCode: currency,
        rate: priced && rate.trim() !== '' ? rateValue : null,
        rateUnit: priced ? rateUnit : 'hour',
        quantity: priced && quantity.trim() !== '' ? quantityValue : null,
        ...(line === null ? {} : { version: line.version }),
      },
    );
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={line === null ? t('dealPlan.dialog.new') : t('dealPlan.dialog.edit')}
      description={t('dealPlan.dialog.hint')}
      closeLabel={t('common.close')}
      width={640}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealPlan.field.name')} required>
          <TextInput
            autoFocus
            value={name}
            onChange={edit(setName)}
            placeholder={t('dealPlan.field.namePlaceholder')}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('dealPlan.column.type')}>
            <Select
              value={type}
              onChange={(event) => {
                setType(event.target.value as DealLineItemType);
                setError(null);
              }}
              options={DEAL_LINE_ITEM_TYPES.map((entry) => ({
                value: entry,
                label: lineTypeLabel(t, entry),
              }))}
            />
          </Field>
          <Field label={t('dealPlan.column.status')}>
            <Select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as DealLineItemStatus);
              }}
              options={DEAL_LINE_ITEM_STATUSES.map((entry) => ({
                value: entry,
                label: lineStatusLabel(t, entry),
              }))}
            />
          </Field>
          <Field label={t('dealPlan.field.milestone')}>
            <Select
              value={milestoneId}
              onChange={(event) => {
                setMilestoneId(event.target.value);
              }}
              options={[
                { value: '', label: t('dealPlan.field.noMilestone') },
                ...deal.milestones.map((milestone) => ({
                  value: milestone.id,
                  label: milestone.name,
                })),
              ]}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealPlan.column.start')}>
            <DateField
              value={startsOn === '' ? null : startsOn}
              onChange={(value) => {
                setStartsOn(value);
                setError(null);
              }}
              label={t('dealPlan.column.start')}
            />
          </Field>
          <Field label={t('dealPlan.column.end')}>
            <DateField
              value={endsOn === '' ? null : endsOn}
              onChange={(value) => {
                setEndsOn(value);
                setError(null);
              }}
              label={t('dealPlan.column.end')}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {hasWork ? (
            <Field label={t('dealPlan.field.estimate')}>
              <TextInput
                inputMode="decimal"
                className="tabular-nums"
                value={estimate}
                placeholder="0:00"
                onChange={edit(setEstimate)}
              />
            </Field>
          ) : null}
          <Field label={t('dealPlan.field.costBudget')}>
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={costBudget}
              placeholder="0"
              onChange={edit(setCostBudget)}
            />
          </Field>
        </div>
        {priced ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('dealPlan.field.rate')}>
              <TextInput
                inputMode="decimal"
                className="tabular-nums"
                value={rate}
                placeholder="0"
                onChange={edit(setRate)}
              />
            </Field>
            <Field label={t('dealPlan.field.rateUnit')}>
              <Select
                value={rateUnit}
                onChange={(event) => {
                  setRateUnit(event.target.value as DealRateUnit);
                }}
                options={DEAL_RATE_UNITS.map((entry) => ({
                  value: entry,
                  label: rateUnitLabel(t, entry),
                }))}
              />
            </Field>
            <Field label={t('dealPlan.field.quantity')}>
              <TextInput
                inputMode="decimal"
                className="tabular-nums"
                value={quantity}
                placeholder="0"
                onChange={edit(setQuantity)}
              />
            </Field>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealPlan.field.amount')}>
            <div className="flex items-center gap-2">
              <TextInput
                inputMode="decimal"
                className="tabular-nums"
                value={amount}
                placeholder="0"
                onChange={edit(setAmount)}
              />
              {/* A convenience, never a rule: the amount stays the figure the client is quoted. */}
              {canCompute ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  title={t('dealPlan.field.computeHint')}
                  aria-label={t('dealPlan.field.computeHint')}
                  onClick={() => {
                    setAmount(String(Math.round(rateValue * quantityValue * 100) / 100));
                    setError(null);
                  }}
                >
                  {t('dealPlan.field.compute')}
                </Button>
              ) : null}
            </div>
          </Field>
          <Field label={t('dealPlan.field.currency')}>
            <Select
              value={currency}
              onChange={(event) => {
                setCurrency(event.target.value);
              }}
              options={currencies.map((code) => ({ value: code, label: code }))}
            />
          </Field>
        </div>

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2 border-t pt-3">
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
